/**
 * Blog post: TELC C1 Hochschule Schreiben Guide with structure, scoring
 * criteria and an essay skeleton with model paragraphs.
 * SEO target: "telc c1 schreiben", "telc c1 hochschule schriftlicher
 * ausdruck", "telc c1 essay beispiel".
 */
import React from 'react';
import RelatedResources from './RelatedResources';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

export default function TelcC1SchreibenGuidePost() {
  const lang = useLangParam();

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        <strong>By the Logami team. Built by German teachers, checked against the official telc scoring criteria.</strong>
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        The <strong>Schriftlicher Ausdruck</strong> in telc C1 Hochschule is the section that decides most
        results: 70 minutes for one structured academic text of around 350 words. Unlike B-level letters,
        you are graded as an academic writer: on argumentation, text architecture, and range of expression.
        This guide explains the task, the four scoring criteria, and a paragraph-by-paragraph essay
        skeleton with model language you can reuse.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The task format
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        You receive <strong>two topics and choose one</strong>. Each topic gives you a short prompt,
        often with a statement or brief input, and a set of aspects your text must address: typically
        presenting the issue, weighing arguments and counterarguments, and formulating your own justified
        position. Plan for <strong>about 350 words in 70 minutes</strong>: roughly 10 minutes planning,
        50 writing, 10 checking.
      </p>
      <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02] mb-10">
        <h3 className="font-space text-lg font-bold text-black mb-3">Quick facts</h3>
        <ul className="list-disc pl-5 text-base text-black/70 leading-relaxed space-y-2">
          <li>70 minutes, one academic text of around 350 words</li>
          <li>Choose 1 of 2 topics; cover every required aspect of the task</li>
          <li>Formal, impersonal register with academic connectors</li>
          <li>Scored on task treatment, structure, expression, and correctness</li>
        </ul>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        What C1 raters actually reward
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Four things separate a passing text from a strong one. <strong>Complete task treatment:</strong>
        every aspect named in the prompt appears in your text with substance. <strong>Visible
        architecture:</strong> a reader can identify Einleitung, argument blocks, and Fazit without effort;
        each paragraph has one job. <strong>Range of expression:</strong> nominal style, Passiv,
        Konjunktiv, and precise academic verbs (<em>sich erweisen als, zur Folge haben, in Frage
        stellen</em>) used naturally rather than stacked. <strong>Correctness under complexity:</strong>
        a correct complex sentence scores; three broken ones cost more than they earn.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The essay skeleton that fits every topic
      </h2>
      <ol className="list-decimal pl-5 text-base md:text-lg text-black/70 leading-relaxed space-y-3 mb-6">
        <li><strong>Einleitung (3 to 4 sentences):</strong> frame the topic's relevance, then state what your text will do</li>
        <li><strong>Argument block A (about 100 words):</strong> the strongest position with an example or consequence</li>
        <li><strong>Argument block B (about 100 words):</strong> the counterposition, taken seriously, then weighed</li>
        <li><strong>Eigene Position + Fazit (about 80 words):</strong> your justified conclusion, referring back to both blocks</li>
      </ol>
      <div className="p-5 sm:p-6 rounded-2xl border-2 border-black bg-white mb-6 font-medium text-black/80 leading-relaxed text-base">
        <p className="mb-3"><strong>Model Einleitung (topic: tuition fees at universities)</strong></p>
        <p className="mb-4">
          Kaum eine hochschulpolitische Frage wird so kontrovers diskutiert wie die Einführung von
          Studiengebühren. Während Befürworter auf die Qualität der Lehre verweisen, warnen Kritiker vor
          einer wachsenden sozialen Ungleichheit. Im Folgenden werden zentrale Argumente beider Seiten
          erörtert, um anschließend eine eigene Position zu begründen.
        </p>
        <p className="mb-3"><strong>Model Fazit</strong></p>
        <p>
          Zusammenfassend lässt sich festhalten, dass Studiengebühren zwar zusätzliche Mittel für die
          Hochschulen bereitstellen könnten, die damit verbundenen sozialen Risiken jedoch schwerer wiegen.
          Aus diesem Grund erscheint ein gebührenfreies Studium, ergänzt durch eine solide staatliche
          Finanzierung, langfristig als der überzeugendere Weg.
        </p>
      </div>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        Both paragraphs demonstrate what raters look for: impersonal framing, Passiv
        (<em>wird diskutiert, werden erörtert</em>), academic connectors (<em>während, zusammenfassend,
        aus diesem Grund</em>), and a clear announcement and closing of the text's structure.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The mistakes that cost the most points
      </h2>
      <ul className="list-disc pl-5 text-base md:text-lg text-black/70 leading-relaxed space-y-3 mb-10">
        <li><strong>Ignoring an aspect of the prompt.</strong> Task treatment is the first criterion raters check.</li>
        <li><strong>An opinion piece instead of an Erörterung.</strong> The counterposition must appear and be weighed.</li>
        <li><strong>Colloquial connectors.</strong> <em>Na ja</em> and <em>total wichtig</em> break the register instantly.</li>
        <li><strong>Uncontrolled length.</strong> 500 words in 70 minutes means no time for checking; errors multiply.</li>
        <li><strong>Memorized universal essays.</strong> Raters recognize prefabricated texts that ignore the concrete topic.</li>
      </ul>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        How to train for it
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Build your academic phrase inventory with the
        {' '}<Link to={`/${lang}/blog/telc-c1-redemittel-schreiben`} className="font-bold text-black underline underline-offset-2 hover:opacity-80">telc C1 Redemittel collection</Link>,
        then write one full essay per week under exam timing and get every text scored. The
        {' '}<Link to={`/${lang}/mock-exams/telc-c1`} className="font-bold text-black underline underline-offset-2 hover:opacity-80">Logami telc C1 mock exam</Link>{' '}
        uses the current international essay format and evaluates your text on the same four criteria, so
        you can watch your structure and expression scores improve from attempt to attempt.
      </p>

      <RelatedResources provider="telc" level="c1" variant="exam-guide" />
    </article>
  );
}
